import React from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { X } from '../../lib/icons';
import { shadows } from '../../lib/styles';

interface AddressStepProps {
  addressInputValue: string;
  setAddressInputValue: (value: string) => void;
  selectedPlace: any;
  selectedPlaceDescription: string | null;
  addressSuggestions: any[];
  isLoadingAddresses: boolean;
  addressError: string | null;
  showAddressSuggestions: boolean;
  setShowAddressSuggestions: (show: boolean) => void;
  selectPlace: (placeId: string, description: string) => Promise<any>;
  clearSelection: () => void;
  onAddressChange: (value: string) => void;
  onAddressClear: () => void;
  addressInputRef: React.RefObject<any>;
  setError: (error: string) => void;
}

export const AddressStep = ({
  addressInputValue,
  setAddressInputValue,
  selectedPlace,
  selectedPlaceDescription,
  addressSuggestions,
  isLoadingAddresses,
  addressError,
  showAddressSuggestions,
  setShowAddressSuggestions,
  selectPlace,
  clearSelection,
  onAddressChange,
  onAddressClear,
  addressInputRef,
  setError
}: AddressStepProps) => {
  const isAddressSelected = !!selectedPlace && !!selectedPlaceDescription;

  return (
    <View style={styles.stepContainer}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Tell us about your home</Text>
        <Text style={styles.stepSubtitle}>
          Enter your street address and we will pull in the rest of the information automatically
        </Text>
      </View>
      <View style={styles.formFields}>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Street Address</Text>
          <View style={styles.addressInputWrapper}>
            <View style={[
              styles.addressInputContainer,
              isAddressSelected && styles.addressInputContainerSelected
            ]}>
              <TextInput
                ref={addressInputRef}
                style={[
                  styles.input,
                  isAddressSelected && styles.inputSelected
                ]}
                value={addressInputValue}
                onChangeText={(value) => {
                  setAddressInputValue(value);
                  onAddressChange(value);
                  setShowAddressSuggestions(true);
                  setError('');
                }}
                onFocus={() => {
                  if (addressSuggestions.length > 0 && !isAddressSelected) {
                    setShowAddressSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding suggestions to allow for clicks
                  setTimeout(() => setShowAddressSuggestions(false), 200);
                }}
                placeholder="Start typing your address..."
                autoCapitalize="words"
                editable={!isAddressSelected}
              />
              {isLoadingAddresses && !isAddressSelected && (
                <View style={styles.addressLoadingIndicator}>
                  <ActivityIndicator size="small" color="#9ca3af" />
                </View>
              )}
              {isAddressSelected && (
                <Pressable
                  style={styles.clearButton}
                  onPress={() => {
                    clearSelection();
                    onAddressClear();
                    setShowAddressSuggestions(false);
                    addressInputRef.current?.focus();
                  }}
                >
                  <X size={18} color="#6b7280" />
                </Pressable>
              )}
            </View>
            {showAddressSuggestions && addressSuggestions.length > 0 && !isAddressSelected && (
              <View style={styles.suggestionsContainer}>
                {addressSuggestions.map((suggestion, index) => (
                  <Pressable
                    key={suggestion.place_id}
                    style={[
                      styles.suggestionItem,
                      index === addressSuggestions.length - 1 && styles.suggestionItemLast
                    ]}
                    onPress={async () => {
                      try {
                        await selectPlace(suggestion.place_id, suggestion.description);
                        setShowAddressSuggestions(false);
                        addressInputRef.current?.blur();
                      } catch (err) {
                        setError('Failed to load address details. Please try again.');
                      }
                    }}
                  >
                    <View style={styles.suggestionContent}>
                      <Text style={styles.suggestionMainText}>
                        {suggestion.structured_formatting.main_text}
                      </Text>
                      <Text style={styles.suggestionSecondaryText}>
                        {suggestion.structured_formatting.secondary_text}
                      </Text>
                    </View>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
          {addressError && addressError.trim().length > 0 ? (
            <Text style={styles.addressErrorText}>{addressError}</Text>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    gap: 32,
  },
  stepHeader: {
    gap: 8,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#4b5563',
  },
  formFields: {
    gap: 16,
  },
  inputGroup: {
    gap: 4,
    ...(Platform.OS === 'web' && {
      flex: 1,
      minWidth: 200,
    }),
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    flex: 1,
  },
  addressInputWrapper: {
    position: 'relative',
  },
  addressInputContainer: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
  },
  addressInputContainerSelected: {
    backgroundColor: '#f0fdf4',
    borderColor: '#86efac',
    borderWidth: 1,
    borderRadius: 8,
  },
  inputSelected: {
    backgroundColor: 'transparent',
    borderWidth: 0,
    flex: 1,
  },
  clearButton: {
    padding: 8,
    marginRight: 8,
    borderRadius: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addressLoadingIndicator: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -10 }],
  },
  addressErrorText: {
    color: '#ef4444',
    fontSize: 12,
    marginTop: 4,
  },
  suggestionsContainer: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    maxHeight: 200,
    ...(Platform.OS === 'web' && shadows.md),
    ...(Platform.OS === 'web' && {
      overflowY: 'auto',
      overflowX: 'hidden',
    }),
    ...(Platform.OS !== 'web' && {
      overflow: 'hidden',
    }),
  },
  suggestionItem: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  suggestionItemLast: {
    borderBottomWidth: 0,
  },
  suggestionContent: {
    gap: 4,
  },
  suggestionMainText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
  },
  suggestionSecondaryText: {
    fontSize: 12,
    color: '#6b7280',
  },
});
